import { useEffect, useState } from "react";
import axios from "axios";
import {
    FaUser,
    FaHotel,
    FaCalendarAlt,
    FaEnvelope,
    FaPhone,
    FaGlobe,
    FaCheckCircle,
    FaTimesCircle,
    FaStar,
    FaEdit,
    FaTrash,
    FaArrowRight,
    FaClock,
    FaLock,
} from "react-icons/fa";

export default function Profile({ onLoginClick }) {
    const [user, setUser] = useState(null);
    const [form, setForm] = useState({});
    const [bookings, setBookings] = useState([]);
    const [activeTab, setActiveTab] = useState("profile");
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);

    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);
    const [now, setNow] = useState(new Date());
    const [cancelLoading, setCancelLoading] = useState(null);

    /* =========================================================
       LIVE CLOCK
    ========================================================= */

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    /* =========================================================
       LOAD USER
    ========================================================= */

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("user"));

        if (!storedUser) {
            setUser(null);
            setBookings([]);
            return;
        }

        setUser(storedUser);

        setForm({
            name: storedUser.name || "",
            email: storedUser.email || "",
            phone: storedUser.phone || "",
            nationality: storedUser.nationality || "",
        });

        fetchBookings(storedUser.id);
    }, []);

    /* =========================================================
       FETCH BOOKINGS
    ========================================================= */

    const fetchBookings = async (userId) => {
        try {
            const res = await axios.get(
                `https://beachhoteldubai.com/api/user/${userId}/bookings`
            );

            setBookings(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    /* =========================================================
       FORM
    ========================================================= */

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const updateProfile = async (e) => {
        e.preventDefault();

        try {
            const res = await axios.put(
                `https://beachhoteldubai.com/api/user/${user.id}`,
                form
            );

            localStorage.setItem(
                "user",
                JSON.stringify(res.data.user)
            );

            setUser(res.data.user);

            alert("Profile updated successfully");
        } catch (err) {
            console.error(err);
            alert("Update failed");
        }
    };

    /* =========================================================
       DATE HELPERS
    ========================================================= */

    const formatDate = (date) =>
        new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });

    const normalizeDate = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const isPastCheckout = (date) => {
        return normalizeDate(date) < normalizeDate(new Date());
    };

    const isFutureCheckIn = (date) => {
        return normalizeDate(date) > normalizeDate(new Date());
    };

    /* =========================================================
       CANCEL BOOKING
    ========================================================= */

    const getCancelDeadline = (booking) => {
        const createdAt = new Date(booking.created_at);
        const checkIn = new Date(booking.check_in);

        const freeCancelUntil = new Date(
            createdAt.getTime() + 24 * 60 * 60 * 1000
        );

        return freeCancelUntil < checkIn
            ? freeCancelUntil
            : checkIn;
    };

    const canCancel = (booking) => {
        return new Date() < getCancelDeadline(booking);
    };

    const getCancelCountdown = (booking) => {
        const currentTime = new Date();
        const deadline = getCancelDeadline(booking);

        const diff = deadline - currentTime;

        if (diff <= 0) return null;

        const totalSeconds = Math.floor(diff / 1000);

        return {
            hours: Math.floor(totalSeconds / 3600),
            minutes: Math.floor((totalSeconds % 3600) / 60),
            seconds: totalSeconds % 60,
        };
    };

    const cancelBooking = async (id) => {
        if (
            !window.confirm(
                "Are you sure you want to cancel this booking?"
            )
        ) {
            return;
        }

        try {
            setCancelLoading(id);

            await axios.post(
                `https://beachhoteldubai.com/api/booking/${id}/cancel`
            );

            alert("Booking cancelled");

            fetchBookings(user.id);
        } catch (err) {
            alert(
                err.response?.data?.message ||
                "Cancel failed"
            );
        } finally {
            setCancelLoading(null);
        }
    };

    /* =========================================================
       REVIEWS
    ========================================================= */

    const openReviewModal = (booking) => {
        setSelectedBooking(booking);
        setRating(0);
        setComment("");
        setShowReviewModal(true);
    };

    const submitReview = async () => {
        if (rating === 0) {
            alert("Please select a rating");
            return;
        }

        setLoading(true);

        try {
            if (selectedBooking.review) {
                await axios.put(
                    `https://beachhoteldubai.com/api/reviews/${selectedBooking.review.id}`,
                    {
                        rating,
                        comment,
                    }
                );

                alert("Review updated!");
            } else {
                await axios.post(
                    "https://beachhoteldubai.com/api/reviews",
                    {
                        booking_id: selectedBooking.id,
                        hotel_id: selectedBooking.hotel_id,
                        email: user.email,
                        rating,
                        comment,
                    }
                );

                alert("Review submitted!");
            }

            setShowReviewModal(false);
            setRating(0);
            setComment("");

            fetchBookings(user.id);
        } catch (err) {
            alert(
                err.response?.data?.message ||
                "Error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteReview = async (reviewId) => {
        if (!window.confirm("Delete this review?")) {
            return;
        }

        try {
            await axios.delete(
                `https://beachhoteldubai.com/api/reviews/${reviewId}`
            );

            alert("Review deleted");

            fetchBookings(user.id);
        } catch (err) {
            alert("Delete failed");
        }
    };

    const handleEditReview = (booking) => {
        setSelectedBooking(booking);
        setRating(booking.review.rating);
        setComment(booking.review.comment);
        setShowReviewModal(true);
    };

    /* =========================================================
       LOGIN STATE
    ========================================================= */

    if (!user) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F8F5F0] px-5 py-16 sm:px-8 lg:px-12">

                <div className="relative w-full max-w-md overflow-hidden rounded-[30px] border border-[#E8D9D5] bg-white shadow-[0_25px_70px_rgba(84,19,30,0.10)]">

                    <div className="h-1 w-full bg-[#B99253]" />

                    <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-[#741F2B]/5 blur-3xl" />

                    <div className="pointer-events-none absolute -left-20 bottom-[-80px] h-48 w-48 rounded-full bg-[#B99253]/10 blur-3xl" />

                    <div className="relative p-8 text-center sm:p-10">

                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#E8D9D5] bg-[#F5EAE7] text-xl text-[#54131E]">
                            <FaLock />
                        </div>

                        <div className="mt-6 flex items-center justify-center gap-3">
                            <span className="h-px w-7 bg-[#B99253]" />

                            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#741F2B]">
                                My Account
                            </span>

                            <span className="h-px w-7 bg-[#B99253]" />
                        </div>

                        <h2 className="mt-5 font-serif text-3xl text-[#54131E] sm:text-4xl">
                            Please Login
                        </h2>

                        <p className="mx-auto mt-4 max-w-sm text-sm leading-7 text-slate-500">
                            Sign in to access your profile, reservations,
                            cancellations and hotel reviews.
                        </p>

                        <button
                            onClick={onLoginClick}
                            className="group mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#54131E] px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#741F2B] hover:shadow-[0_12px_30px_rgba(84,19,30,0.18)]"
                        >
                            Go to Login

                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#B99253] text-[#54131E]">
                                <FaArrowRight className="text-[10px] transition-transform duration-300 group-hover:translate-x-1" />
                            </span>
                        </button>

                    </div>
                </div>
            </div>
        );
    }

    /* =========================================================
       MAIN
    ========================================================= */

    return (
        <div className="min-h-screen bg-[#F8F5F0]">

            {/* =====================================================
                ACCOUNT HERO
            ===================================================== */}

            <section className="relative overflow-hidden bg-[#54131E]">

                <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#741F2B]/50 blur-3xl" />

                <div className="pointer-events-none absolute -left-32 bottom-[-140px] h-96 w-96 rounded-full bg-[#B99253]/10 blur-3xl" />

                <div className="pointer-events-none absolute right-[12%] top-1/2 hidden h-48 w-48 -translate-y-1/2 rounded-full border border-white/5 lg:block" />

                <div className="pointer-events-none absolute right-[14%] top-1/2 hidden h-28 w-28 -translate-y-1/2 rounded-full border border-[#B99253]/25 lg:block" />

                <div className="relative z-10 mx-auto max-w-[1440px] px-5 pb-12 pt-[120px] sm:px-8 sm:pb-14 lg:px-12 lg:pb-16 xl:px-16">

                    <div className="max-w-3xl">

                        <div className="flex items-center gap-3">
                            <span className="h-px w-9 bg-[#B99253]" />

                            <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#E7C78B]">
                                My Account
                            </span>
                        </div>

                        <h1 className="mt-5 font-serif text-4xl leading-tight text-white sm:text-5xl lg:text-[58px]">
                            Welcome,

                            <span className="block text-[#E7C78B]">
                                {user.name}
                            </span>
                        </h1>

                        <p className="mt-5 max-w-2xl text-sm leading-7 text-white/65 sm:text-base">
                            Manage your personal information, hotel
                            reservations, cancellations and reviews —
                            all from one place.
                        </p>

                        <div className="mt-7 flex flex-wrap gap-3">

                            <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 backdrop-blur-sm">
                                <FaCheckCircle className="text-[#E7C78B]" />

                                <span className="text-xs font-semibold text-white/80">
                                    Account Active
                                </span>
                            </div>

                            <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 backdrop-blur-sm">
                                <FaHotel className="text-[#E7C78B]" />

                                <span className="text-xs font-semibold text-white/80">
                                    {bookings.length}{" "}
                                    {bookings.length === 1
                                        ? "Booking"
                                        : "Bookings"}
                                </span>
                            </div>

                        </div>

                    </div>
                </div>

                <div className="absolute bottom-0 left-0 h-px w-full bg-[#B99253]" />

            </section>


            {/* =====================================================
                ACCOUNT CONTENT
            ===================================================== */}

            <section className="relative overflow-hidden py-12 sm:py-14 lg:py-16">

                <div className="pointer-events-none absolute -right-48 top-0 h-[500px] w-[500px] rounded-full bg-[#741F2B]/5 blur-3xl" />

                <div className="pointer-events-none absolute -left-48 bottom-0 h-[500px] w-[500px] rounded-full bg-[#B99253]/10 blur-3xl" />

                <div className="relative z-10 mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

                    {/* Section Heading */}

                    <div className="mb-9 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">

                        <div>

                            <div className="flex items-center gap-3">
                                <span className="h-px w-8 bg-[#B99253]" />

                                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#741F2B]">
                                    Account Dashboard
                                </p>
                            </div>

                            <h2 className="mt-4 font-serif text-3xl text-[#54131E] sm:text-4xl">
                                Manage Your Account
                            </h2>

                            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
                                Update your details or manage your hotel
                                reservations from your account dashboard.
                            </p>

                        </div>

                        <div className="flex w-fit items-center gap-4 rounded-[20px] border border-[#E8D9D5] bg-white px-5 py-4 shadow-[0_10px_35px_rgba(84,19,30,0.05)]">

                            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#54131E] text-[#E7C78B]">
                                <FaHotel />
                            </div>

                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#B99253]">
                                    Total Bookings
                                </p>

                                <p className="mt-1 font-serif text-2xl text-[#54131E]">
                                    {bookings.length}
                                </p>
                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        LAYOUT
                    ================================================= */}

                    <div className="grid gap-7 lg:grid-cols-[270px_1fr]">

                        {/* SIDEBAR */}

                        <aside>

                            <div className="sticky top-24 overflow-hidden rounded-[26px] border border-[#E8D9D5] bg-white p-5 shadow-[0_15px_45px_rgba(84,19,30,0.06)]">

                                <div className="mb-6 rounded-[20px] border border-[#E8D9D5] bg-[#F5EAE7] p-4">

                                    <div className="flex items-center gap-3">

                                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#54131E] text-lg text-[#E7C78B]">
                                            <FaUser />
                                        </div>

                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-bold text-[#54131E]">
                                                {user.name}
                                            </p>

                                            <p className="truncate text-xs text-slate-500">
                                                {user.email}
                                            </p>
                                        </div>

                                    </div>
                                </div>

                                <div className="mb-4">

                                    <div className="flex items-center gap-2">
                                        <span className="h-px w-5 bg-[#B99253]" />

                                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#741F2B]">
                                            Account
                                        </p>
                                    </div>

                                    <h3 className="mt-2 font-serif text-xl text-[#54131E]">
                                        My Account
                                    </h3>

                                </div>


                                {/* Profile Tab */}

                                <button
                                    onClick={() => setActiveTab("profile")}
                                    className={`group mb-2 flex w-full items-center gap-3 rounded-[15px] px-3 py-3 text-left text-xs font-semibold transition-all duration-200 ${activeTab === "profile"
                                        ? "bg-[#54131E] text-white shadow-[0_8px_20px_rgba(84,19,30,0.14)]"
                                        : "text-slate-500 hover:bg-[#F5EAE7] hover:text-[#54131E]"
                                        }`}
                                >

                                    <span
                                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${activeTab === "profile"
                                            ? "bg-[#B99253] text-[#54131E]"
                                            : "bg-[#F5EAE7] text-[#741F2B]"
                                            }`}
                                    >
                                        <FaUser className="text-xs" />
                                    </span>

                                    <span className="flex-1">
                                        Profile
                                    </span>

                                    <FaArrowRight
                                        className={`text-[9px] transition-all ${activeTab === "profile"
                                            ? "translate-x-0 opacity-100"
                                            : "opacity-0 group-hover:translate-x-0.5 group-hover:opacity-100"
                                            }`}
                                    />

                                </button>


                                {/* Bookings Tab */}

                                <button
                                    onClick={() => setActiveTab("bookings")}
                                    className={`group flex w-full items-center gap-3 rounded-[15px] px-3 py-3 text-left text-xs font-semibold transition-all duration-200 ${activeTab === "bookings"
                                        ? "bg-[#54131E] text-white shadow-[0_8px_20px_rgba(84,19,30,0.14)]"
                                        : "text-slate-500 hover:bg-[#F5EAE7] hover:text-[#54131E]"
                                        }`}
                                >

                                    <span
                                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${activeTab === "bookings"
                                            ? "bg-[#B99253] text-[#54131E]"
                                            : "bg-[#F5EAE7] text-[#741F2B]"
                                            }`}
                                    >
                                        <FaHotel className="text-xs" />
                                    </span>

                                    <span className="flex-1">
                                        My Bookings
                                    </span>

                                    <span
                                        className={`rounded-full px-2.5 py-1 text-[9px] font-bold ${activeTab === "bookings"
                                            ? "bg-[#B99253] text-[#54131E]"
                                            : "bg-[#F5EAE7] text-[#741F2B]"
                                            }`}
                                    >
                                        {bookings.length}
                                    </span>

                                </button>

                            </div>

                        </aside>


                        {/* MAIN CONTENT */}

                        <main>

                            {/* PROFILE TAB */}

                            {activeTab === "profile" && (

                                <div className="overflow-hidden rounded-[28px] border border-[#E8D9D5] bg-white shadow-[0_15px_50px_rgba(84,19,30,0.06)]">

                                    <div className="h-1 w-full bg-[#B99253]" />

                                    <div className="p-6 sm:p-8 lg:p-10">

                                        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

                                            <div className="flex items-center gap-4">

                                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F5EAE7] text-[#54131E]">
                                                    <FaUser />
                                                </div>

                                                <div>
                                                    <h2 className="font-serif text-2xl text-[#54131E] sm:text-3xl">
                                                        Profile Information
                                                    </h2>

                                                    <p className="mt-1 text-xs text-slate-400">
                                                        Keep your account information up to date.
                                                    </p>
                                                </div>

                                            </div>

                                            <div className="flex w-fit items-center gap-2 rounded-full border border-[#E8D9D5] bg-[#F5EAE7] px-3 py-2 text-[10px] font-bold text-[#741F2B]">
                                                <FaCheckCircle className="text-[#B99253]" />
                                                Account Active
                                            </div>

                                        </div>


                                        <form
                                            onSubmit={updateProfile}
                                            className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                                        >

                                            {/* Name */}

                                            <div>
                                                <label className="mb-2 block text-xs font-bold text-[#54131E]">
                                                    Full Name
                                                </label>

                                                <div className="relative">
                                                    <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-[#741F2B]" />

                                                    <input
                                                        name="name"
                                                        value={form.name || ""}
                                                        onChange={handleChange}
                                                        className="w-full rounded-[14px] border border-[#E8D9D5] bg-[#F8F5F0] py-3.5 pl-11 pr-4 text-sm text-[#54131E] outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#B99253] focus:bg-white focus:ring-4 focus:ring-[#B99253]/10"
                                                        placeholder="Full Name"
                                                    />
                                                </div>
                                            </div>


                                            {/* Email */}

                                            <div>
                                                <label className="mb-2 block text-xs font-bold text-[#54131E]">
                                                    Email Address
                                                </label>

                                                <div className="relative">
                                                    <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-[#741F2B]" />

                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={form.email || ""}
                                                        onChange={handleChange}
                                                        className="w-full rounded-[14px] border border-[#E8D9D5] bg-[#F8F5F0] py-3.5 pl-11 pr-4 text-sm text-[#54131E] outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#B99253] focus:bg-white focus:ring-4 focus:ring-[#B99253]/10"
                                                        placeholder="Email Address"
                                                    />
                                                </div>
                                            </div>


                                            {/* Phone */}

                                            <div>
                                                <label className="mb-2 block text-xs font-bold text-[#54131E]">
                                                    Phone Number
                                                </label>

                                                <div className="relative">
                                                    <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-[#741F2B]" />

                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        value={form.phone || ""}
                                                        onChange={handleChange}
                                                        className="w-full rounded-[14px] border border-[#E8D9D5] bg-[#F8F5F0] py-3.5 pl-11 pr-4 text-sm text-[#54131E] outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#B99253] focus:bg-white focus:ring-4 focus:ring-[#B99253]/10"
                                                        placeholder="Phone Number"
                                                    />
                                                </div>
                                            </div>


                                            {/* Nationality */}

                                            <div>
                                                <label className="mb-2 block text-xs font-bold text-[#54131E]">
                                                    Nationality
                                                </label>

                                                <div className="relative">
                                                    <FaGlobe className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-[#741F2B]" />

                                                    <input
                                                        name="nationality"
                                                        value={form.nationality || ""}
                                                        onChange={handleChange}
                                                        className="w-full rounded-[14px] border border-[#E8D9D5] bg-[#F8F5F0] py-3.5 pl-11 pr-4 text-sm text-[#54131E] outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#B99253] focus:bg-white focus:ring-4 focus:ring-[#B99253]/10"
                                                        placeholder="Nationality"
                                                    />
                                                </div>
                                            </div>


                                            {/* Submit */}

                                            <div className="pt-3 sm:col-span-2">

                                                <button
                                                    type="submit"
                                                    className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#54131E] px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#741F2B] hover:shadow-[0_12px_30px_rgba(84,19,30,0.18)] sm:w-auto"
                                                >
                                                    Update Profile

                                                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#B99253] text-[#54131E]">
                                                        <FaArrowRight className="text-[10px] transition-transform duration-300 group-hover:translate-x-1" />
                                                    </span>
                                                </button>

                                            </div>

                                        </form>

                                    </div>

                                </div>

                            )}


                            {/* BOOKINGS TAB */}

                            {activeTab === "bookings" && (

                                <div className="overflow-hidden rounded-[28px] border border-[#E8D9D5] bg-white shadow-[0_15px_50px_rgba(84,19,30,0.06)]">

                                    <div className="h-1 w-full bg-[#B99253]" />

                                    <div className="p-6 sm:p-8 lg:p-10">

                                        <div className="mb-8 flex flex-col gap-5 md:flex-row md:items-center md:justify-between">

                                            <div className="flex items-center gap-4">

                                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F5EAE7] text-[#54131E]">
                                                    <FaHotel />
                                                </div>

                                                <div>
                                                    <h2 className="font-serif text-2xl text-[#54131E] sm:text-3xl">
                                                        My Bookings
                                                    </h2>

                                                    <p className="mt-1 text-sm text-slate-400">
                                                        View, manage cancellations and leave reviews.
                                                    </p>
                                                </div>

                                            </div>

                                            <div className="flex w-fit items-center gap-3 rounded-[18px] border border-[#E8D9D5] bg-[#F5EAE7] px-4 py-3">

                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#54131E] text-[#E7C78B]">
                                                    <FaCalendarAlt className="text-sm" />
                                                </div>

                                                <div>
                                                    <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#B99253]">
                                                        Total Bookings
                                                    </p>

                                                    <p className="font-serif text-xl text-[#54131E]">
                                                        {bookings.length}
                                                    </p>
                                                </div>

                                            </div>

                                        </div>


                                        {/* Empty State */}

                                        {bookings.length === 0 ? (

                                            <div className="rounded-[24px] border border-[#E8D9D5] bg-[#F8F5F0] py-20 text-center">

                                                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#54131E] text-xl text-[#E7C78B]">
                                                    <FaHotel />
                                                </div>

                                                <div className="mt-6 flex items-center justify-center gap-3">
                                                    <span className="h-px w-6 bg-[#B99253]" />

                                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#741F2B]">
                                                        Reservations
                                                    </span>

                                                    <span className="h-px w-6 bg-[#B99253]" />
                                                </div>

                                                <h3 className="mt-4 font-serif text-2xl text-[#54131E]">
                                                    No bookings yet
                                                </h3>

                                                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-400">
                                                    Your upcoming reservations will appear here once you make a booking.
                                                </p>

                                            </div>

                                        ) : (

                                            <div className="space-y-6">

                                                {bookings.map((b) => (

                                                    <div
                                                        key={b.id}
                                                        className="overflow-hidden rounded-[26px] border border-[#E8D9D5] bg-white shadow-[0_10px_35px_rgba(84,19,30,0.04)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_45px_rgba(84,19,30,0.08)]"
                                                    >

                                                        {/* Booking Header */}

                                                        <div className="border-b border-[#E8D9D5] bg-[#F5EAE7] px-5 py-5 sm:px-6">

                                                            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">

                                                                <div className="flex items-start gap-4">

                                                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#54131E] text-[#E7C78B]">
                                                                        <FaHotel />
                                                                    </div>

                                                                    <div>
                                                                        <p className="mb-1 text-[9px] font-bold uppercase tracking-[0.2em] text-[#B99253]">
                                                                            Reservation
                                                                        </p>

                                                                        <h3 className="font-serif text-xl text-[#54131E] sm:text-2xl">
                                                                            {b.hotel_name}
                                                                        </h3>

                                                                        <p className="mt-1 text-xs text-slate-500 sm:text-sm">
                                                                            {b.room_name}{" "}
                                                                            <span className="mx-1 text-[#B99253]">
                                                                                •
                                                                            </span>
                                                                            Room {b.room_number}
                                                                        </p>
                                                                    </div>

                                                                </div>


                                                                {/* Status */}

                                                                <div>
                                                                    <span
                                                                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[9px] font-bold uppercase tracking-[0.16em] ${b.status === "confirmed"
                                                                            ? "bg-[#54131E] text-white"
                                                                            : b.status === "cancelled"
                                                                                ? "bg-red-50 text-red-600"
                                                                                : "bg-[#FFF4D6] text-[#8A6330]"
                                                                            }`}
                                                                    >
                                                                        {b.status === "confirmed" ? (
                                                                            <FaCheckCircle className="text-[#E7C78B]" />
                                                                        ) : b.status === "cancelled" ? (
                                                                            <FaTimesCircle />
                                                                        ) : (
                                                                            <FaClock />
                                                                        )}

                                                                        {b.status}
                                                                    </span>
                                                                </div>

                                                            </div>

                                                        </div>


                                                        {/* Booking Content */}

                                                        <div className="p-5 sm:p-6">

                                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">

                                                                {/* Check in */}

                                                                <div className="rounded-[18px] border border-[#E8D9D5] bg-[#F8F5F0] p-4">

                                                                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#54131E] text-[#E7C78B]">
                                                                        <FaCalendarAlt className="text-xs" />
                                                                    </div>

                                                                    <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#B99253]">
                                                                        Check-in
                                                                    </p>

                                                                    <p className="mt-1 text-sm font-bold text-[#54131E]">
                                                                        {formatDate(b.check_in)}
                                                                    </p>

                                                                </div>


                                                                {/* Check out */}

                                                                <div className="rounded-[18px] border border-[#E8D9D5] bg-[#F8F5F0] p-4">

                                                                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#54131E] text-[#E7C78B]">
                                                                        <FaCalendarAlt className="text-xs" />
                                                                    </div>

                                                                    <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#B99253]">
                                                                        Check-out
                                                                    </p>

                                                                    <p className="mt-1 text-sm font-bold text-[#54131E]">
                                                                        {formatDate(b.check_out)}
                                                                    </p>

                                                                </div>


                                                                {/* Guests */}

                                                                <div className="rounded-[18px] border border-[#E8D9D5] bg-[#F8F5F0] p-4">

                                                                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#54131E] text-[#E7C78B]">
                                                                        <FaUser className="text-xs" />
                                                                    </div>

                                                                    <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#B99253]">
                                                                        Guests
                                                                    </p>

                                                                    <p className="mt-1 text-sm font-bold text-[#54131E]">
                                                                        {b.guests} Guests
                                                                    </p>

                                                                </div>


                                                                {/* Amount */}

                                                                <div className="rounded-[18px] border border-[#B99253]/30 bg-[#F5EAE7] p-4">

                                                                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#B99253] text-[#54131E]">
                                                                        <span className="text-[9px] font-bold">
                                                                            AED
                                                                        </span>
                                                                    </div>

                                                                    <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A6330]">
                                                                        Amount Paid
                                                                    </p>

                                                                    <p className="mt-1 text-sm font-bold text-[#54131E]">
                                                                        AED {b.total_amount}
                                                                    </p>

                                                                </div>

                                                            </div>


                                                            {/* Actions */}

                                                            <div className="mt-6 flex flex-wrap items-center gap-3">

                                                                {/* Cancel */}

                                                                {b.status === "confirmed" &&
                                                                    canCancel(b) && (
                                                                        <>

                                                                            <button
                                                                                onClick={() =>
                                                                                    cancelBooking(b.id)
                                                                                }
                                                                                disabled={
                                                                                    cancelLoading === b.id
                                                                                }
                                                                                className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold transition-all ${cancelLoading === b.id
                                                                                    ? "cursor-not-allowed bg-red-300 text-white"
                                                                                    : "border border-red-100 bg-red-50 text-red-600 hover:bg-red-100"
                                                                                    }`}
                                                                            >

                                                                                {cancelLoading === b.id ? (
                                                                                    <>
                                                                                        <svg
                                                                                            className="h-4 w-4 animate-spin"
                                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                                            fill="none"
                                                                                            viewBox="0 0 24 24"
                                                                                        >
                                                                                            <circle
                                                                                                className="opacity-25"
                                                                                                cx="12"
                                                                                                cy="12"
                                                                                                r="10"
                                                                                                stroke="currentColor"
                                                                                                strokeWidth="4"
                                                                                            />

                                                                                            <path
                                                                                                className="opacity-75"
                                                                                                fill="currentColor"
                                                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                                                            />
                                                                                        </svg>

                                                                                        Cancelling...
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FaTimesCircle />
                                                                                        Cancel Booking
                                                                                    </>
                                                                                )}

                                                                            </button>


                                                                            {/* Countdown */}

                                                                            {(() => {
                                                                                const time = getCancelCountdown(b);

                                                                                if (!time) return null;

                                                                                return (
                                                                                    <div className="inline-flex items-center gap-2 rounded-full border border-[#E8D9D5] bg-[#F5EAE7] px-4 py-2.5 text-[10px] font-bold text-[#8A6330]">

                                                                                        <FaClock className="text-[#B99253]" />

                                                                                        Free cancellation ends in{" "}

                                                                                        <span className="font-bold text-[#54131E]">
                                                                                            {time.hours
                                                                                                .toString()
                                                                                                .padStart(2, "0")}
                                                                                            :
                                                                                            {time.minutes
                                                                                                .toString()
                                                                                                .padStart(2, "0")}
                                                                                            :
                                                                                            {time.seconds
                                                                                                .toString()
                                                                                                .padStart(2, "0")}
                                                                                        </span>

                                                                                    </div>
                                                                                );
                                                                            })()}

                                                                        </>
                                                                    )}


                                                                {/* Leave Review */}

                                                                {b.status === "confirmed" &&
                                                                    isPastCheckout(b.check_out) &&
                                                                    !b.review && (

                                                                        <button
                                                                            onClick={() =>
                                                                                openReviewModal(b)
                                                                            }
                                                                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#B99253] px-5 py-2.5 text-xs font-bold text-[#54131E] transition-all hover:bg-[#E7C78B] hover:shadow-[0_8px_20px_rgba(185,146,83,0.18)]"
                                                                        >
                                                                            <FaStar />
                                                                            Leave Review
                                                                        </button>

                                                                    )}

                                                            </div>


                                                            {/* Review Availability */}

                                                            {b.status === "confirmed" &&
                                                                !isPastCheckout(b.check_out) && (

                                                                    <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                                                                        <FaClock className="text-[#B99253]" />
                                                                        Reviews can be submitted after checkout.
                                                                    </div>

                                                                )}


                                                            {/* Review */}

                                                            {b.review && (

                                                                <div className="mt-6 overflow-hidden rounded-[20px] border border-[#E8D9D5] bg-[#F8F5F0]">

                                                                    <div className="border-b border-[#E8D9D5] px-5 py-4">

                                                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

                                                                            <div className="flex items-center gap-3">

                                                                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#54131E] text-[#E7C78B]">
                                                                                    <FaStar className="text-xs" />
                                                                                </div>

                                                                                <h4 className="font-serif text-lg text-[#54131E]">
                                                                                    Your Review
                                                                                </h4>

                                                                            </div>

                                                                            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#F5EAE7] px-3 py-1.5 text-xs font-bold text-[#8A6330]">
                                                                                <FaStar />
                                                                                {b.review.rating}/5
                                                                            </span>

                                                                        </div>

                                                                    </div>

                                                                    <div className="p-5">

                                                                        <p className="text-sm leading-7 text-slate-500">
                                                                            {b.review.comment}
                                                                        </p>

                                                                        <div className="mt-5 flex flex-wrap gap-3">

                                                                            <button
                                                                                onClick={() =>
                                                                                    handleEditReview(b)
                                                                                }
                                                                                className="inline-flex items-center gap-2 rounded-full bg-[#54131E] px-4 py-2 text-xs font-bold text-white transition hover:bg-[#741F2B]"
                                                                            >
                                                                                <FaEdit />
                                                                                Edit Review
                                                                            </button>

                                                                            <button
                                                                                onClick={() =>
                                                                                    handleDeleteReview(b.review.id)
                                                                                }
                                                                                className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-red-50 px-4 py-2 text-xs font-bold text-red-600 transition hover:bg-red-100"
                                                                            >
                                                                                <FaTrash />
                                                                                Delete Review
                                                                            </button>

                                                                        </div>

                                                                    </div>

                                                                </div>

                                                            )}

                                                        </div>

                                                    </div>

                                                ))}

                                            </div>

                                        )}

                                    </div>

                                </div>

                            )}

                        </main>

                    </div>

                </div>

            </section>


            {/* =====================================================
                REVIEW MODAL
            ===================================================== */}

            {showReviewModal && (

                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#54131E]/85 px-4 py-6 backdrop-blur-md">

                    <div className="pointer-events-none absolute -left-20 top-1/4 h-60 w-60 rounded-full bg-[#B99253]/10 blur-3xl" />

                    <div className="pointer-events-none absolute -right-20 bottom-1/4 h-60 w-60 rounded-full bg-[#741F2B]/25 blur-3xl" />

                    <div className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-[#E8D9D5] bg-white shadow-[0_30px_90px_rgba(0,0,0,0.25)]">

                        <div className="h-1 w-full bg-[#B99253]" />

                        <div className="p-6 sm:p-8">

                            {/* Header */}

                            <div className="flex items-start justify-between gap-4">

                                <div>

                                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#54131E] text-[#E7C78B]">
                                        <FaStar />
                                    </div>

                                    <div className="mt-5 flex items-center gap-2">
                                        <span className="h-px w-6 bg-[#B99253]" />

                                        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#741F2B]">
                                            Guest Experience
                                        </span>
                                    </div>

                                    <h2 className="mt-3 font-serif text-2xl text-[#54131E] sm:text-3xl">
                                        {selectedBooking?.review
                                            ? "Edit Review"
                                            : "Add Review"}
                                    </h2>

                                    <p className="mt-1 text-xs text-slate-400">
                                        {selectedBooking?.hotel_name}
                                    </p>

                                </div>

                                <button
                                    onClick={() => setShowReviewModal(false)}
                                    aria-label="Close review modal"
                                    className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E8D9D5] bg-[#F8F5F0] text-slate-400 transition hover:border-[#B99253] hover:text-[#54131E]"
                                >
                                    <FaTimesCircle />
                                </button>

                            </div>


                            {/* Rating */}

                            <div className="mt-7">

                                <p className="mb-3 text-xs font-bold text-[#54131E]">
                                    Your Rating
                                </p>

                                <div className="flex items-center gap-2">

                                    {[1, 2, 3, 4, 5].map((star) => (

                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setRating(star)}
                                            aria-label={`Rate ${star} out of 5 stars`}
                                            className={`flex h-11 w-11 items-center justify-center rounded-full transition-all ${star <= rating
                                                ? "scale-105 bg-[#B99253] text-[#54131E]"
                                                : "bg-[#F5EAE7] text-[#D5C5BF] hover:bg-[#F0E1DC] hover:text-[#B99253]"
                                                }`}
                                        >
                                            <FaStar />
                                        </button>

                                    ))}

                                </div>

                            </div>


                            {/* Comment */}

                            <div className="mt-6">

                                <label className="mb-2 block text-xs font-bold text-[#54131E]">
                                    Your Experience
                                </label>

                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Write your experience..."
                                    className="w-full resize-none rounded-[16px] border border-[#E8D9D5] bg-[#F8F5F0] p-4 text-sm leading-6 text-[#54131E] outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#B99253] focus:bg-white focus:ring-4 focus:ring-[#B99253]/10"
                                    rows="4"
                                />

                            </div>


                            {/* Submit */}

                            <button
                                onClick={submitReview}
                                disabled={loading}
                                className={`group mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#54131E] px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 ${loading
                                    ? "cursor-not-allowed opacity-70"
                                    : "hover:bg-[#741F2B] hover:shadow-[0_12px_30px_rgba(84,19,30,0.18)]"
                                    }`}
                            >

                                {loading
                                    ? "Submitting..."
                                    : selectedBooking?.review
                                        ? "Update Review"
                                        : "Submit Review"}

                                {!loading && (
                                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#B99253] text-[#54131E]">
                                        <FaArrowRight className="text-[10px] transition-transform duration-300 group-hover:translate-x-1" />
                                    </span>
                                )}

                            </button>


                            {/* Close */}

                            <button
                                onClick={() => setShowReviewModal(false)}
                                className="mt-3 w-full rounded-full border border-[#E8D9D5] bg-[#F8F5F0] px-5 py-3 text-xs font-bold text-slate-500 transition hover:border-[#B99253] hover:text-[#54131E]"
                            >
                                Close
                            </button>

                        </div>

                    </div>

                </div>

            )}

        </div>
    );
}